import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { SxProps } from '@mui/system';
import { ReactNode, useEffect, useState } from 'react';
import { useGetUserMeQuery } from '../../services/entities/impl/blfApi/endpoints/users';
import { darkenColor, hasAuthorizedRole } from '../commonUtils';
import {
  SendStatus,
  SendStatusEnum,
  initialSendStatus,
} from '../onboarding/onboardingUtils';
import { flexCenter } from './cssClasses';

interface Props {
  icon: ReactNode;
  handleClick:
    | ((event: React.MouseEvent<HTMLElement>) => void)
    | (() => Promise<void>);
  ariaLabel?: string;
  authorizedRoles?: string[];
  sx?: SxProps;
  confirmationDialog?: boolean;
  confirmationMessage?: string;
  disabled?: boolean;
  sendStatus?: SendStatus;
  size?: 'small' | 'medium' | 'large';
}

const IconButtonProtected: React.FC<Props> = ({
  icon,
  handleClick,
  ariaLabel = 'button',
  authorizedRoles = [],
  sx = {},
  confirmationDialog = false,
  confirmationMessage = 'Are you sure?',
  sendStatus = { ...initialSendStatus },
  size,
}) => {
  const theme = useTheme();
  const { data: user } = useGetUserMeQuery();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const enforceAuthorization = authorizedRoles.length > 0;

  let isAuthorized = true;
  if (enforceAuthorization) {
    isAuthorized = hasAuthorizedRole(authorizedRoles, user);
  }

  const handleClose = (e: React.SyntheticEvent) => {
    if (e.stopPropagation) {
      e.stopPropagation();
    }
    setConfirmDialogOpen(false);
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setConfirmDialogOpen(true);
  };

  const handleAction = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    handleClick(e);
  };

  // if success, show success message for 2 seconds then close dialog
  useEffect(() => {
    if (sendStatus.status === SendStatusEnum.SUCCESS) {
      setTimeout(() => {
        handleClose({} as React.MouseEvent<HTMLElement>);
      }, 2000);
    }
  }, [sendStatus.status]);

  const buttonStyles: SxProps = {
    width: '100%',
    m: '0.5rem',
    color: theme.colors.common.white,
    bgcolor: theme.colors.secondary,
    '&:hover': {
      bgcolor: darkenColor(theme.colors.secondary, 0.2),
    },
  };

  const iconButtonContent = (
    <IconButton
      aria-label={ariaLabel}
      sx={{ ...sx }}
      size={size}
      onClick={confirmationDialog ? handleOpen : handleClick}
      disabled={!isAuthorized}
    >
      {icon}
    </IconButton>
  );

  return (
    <Box
      sx={{
        width: 'min-content',
        height: 'min-content',
        p: 0,
        m: 0,
        ...flexCenter,
      }}
    >
      {isAuthorized ? (
        iconButtonContent
      ) : (
        <Tooltip title='Not authorized' placement='bottom'>
          <span>{iconButtonContent}</span>
        </Tooltip>
      )}
      <Dialog onClose={handleClose} open={confirmDialogOpen}>
        <DialogTitle sx={{ p: '2rem', ...flexCenter }}>
          {confirmationMessage}
        </DialogTitle>
        {sendStatus.message && (
          <Typography
            variant='h5'
            color='text.secondary'
            noWrap
            sx={{ width: '100%', ...flexCenter }}
          >
            {sendStatus.message}
          </Typography>
        )}
        <DialogActions sx={{ ...flexCenter }}>
          <Button onClick={handleClose} sx={{ ...buttonStyles }} size='large'>
            Cancel
          </Button>
          <Button
            onClick={handleAction}
            sx={{ ...buttonStyles }}
            size='large'
            disabled={sendStatus.status === SendStatusEnum.LOADING}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default IconButtonProtected;
